<template>
  <div>
    <!-- Survey Upload Method Selector -->
    <div class="survey-method-selector p-1 rounded ">
      <label class="font-weight-bold d-flex align-items-center">
        <span>طريقة الرفع المساحي:</span>
        <b-spinner
          v-if="loadingSurveyMethods || savingSurveyMethod"
          small
          class="mr-2"
          variant="primary"
        />
      </label>
      <b-form-radio-group
        v-model="surveyUploadMethodId"
        :options="surveyUploadMethods"
        class="mb-2"
        :disabled="loadingSurveyMethods || savingSurveyMethod"
        @change="onSurveyMethodChange"
      />
    </div>

    <!-- Conditional Fields Based on Upload Method -->
    <div
      v-if="surveyUploadMethodId == 2"
      class="mb-3 p-3 bg-light rounded"
    >
      <b-form-group label="اسم النقطة الثابتة">
        <b-form-select
          v-model="selectedFixedPointId"
          :options="fixedPointsOptions"
          @change="onFixedPointSelected"
        >
          <template #first>
            <b-form-select-option :value="null">
              اختر النقطة الثابتة
            </b-form-select-option>
          </template>
        </b-form-select>
      </b-form-group>
      <div
        v-if="loadingSelectedFixedPointMedia"
        class="text-center my-2"
      >
        <b-spinner
          small
          variant="primary"
        />
      </div>
      <div
        v-else-if="selectedFixedPointRawDataMedia || selectedFixedPointProjectMedia"
        class="mt-2 d-flex flex-wrap"
      >
        <b-card
          v-if="selectedFixedPointRawDataMedia"
          class="media-card mb-1 shadow-sm ml-1"
          body-class="p-1 d-flex flex-column align-items-center"
          style="width: 220px; min-height: 260px;"
        >
          <div class="w-100 text-center">
            <div
              class="file-title font-weight-bold"
              style="word-break:break-word"
            >
              ملف Raw Data للنقطة الثابتة
            </div>
          </div>
          <div class="media-file-details w-100 text-center">
            <div class="file-meta text-muted small mt-1">
              {{ (selectedFixedPointRawDataMedia.extension || '').toUpperCase() }} • {{ selectedFixedPointRawDataMedia.human_readable_size || '--' }}
            </div>
            <div class="file-meta text-muted small mt-1">
              {{ selectedFixedPointRawDataMedia.file_name || selectedFixedPointRawDataMedia.name || '--' }}
            </div>
            <div class="file-meta text-muted small mt-1">
              {{ selectedFixedPointRawDataMedia.uploader ? selectedFixedPointRawDataMedia.uploader.name : '' }}
            </div>
            <div class="file-meta text-muted small mt-1">
              {{ selectedFixedPointRawDataMedia.created_at | formatJalali }}
            </div>
          </div>
          <div class="media-card-actions d-flex flex-row justify-content-center gap-1 w-100">
            <b-button
              v-if="selectedFixedPointRawDataMedia.is_image || selectedFixedPointRawDataMedia.is_video || selectedFixedPointRawDataMedia.is_document"
              v-b-tooltip.hover.top="'معاينة'"
              :href="selectedFixedPointRawDataMedia.url"
              target="_blank"
              size="sm"
              variant="info"
            >
              <feather-icon icon="EyeIcon" size="12" />
            </b-button>
            <b-button
              v-b-tooltip.hover.top="'تحميل'"
              :href="selectedFixedPointRawDataMedia.download_url || selectedFixedPointRawDataMedia.url"
              target="_blank"
              size="sm"
              variant="success"
            >
              <feather-icon icon="DownloadIcon" size="12" />
            </b-button>
          </div>
        </b-card>

        <b-card
          v-if="selectedFixedPointProjectMedia"
          class="media-card mb-1 shadow-sm"
          body-class="p-1 d-flex flex-column align-items-center"
          style="width: 220px; min-height: 260px;"
        >
          <div class="w-100 text-center">
            <div
              class="file-title font-weight-bold"
              style="word-break:break-word"
            >
              ملف المشروع (project)
            </div>
          </div>
          <div class="media-file-details w-100 text-center">
            <div class="file-meta text-muted small mt-1">
              {{ (selectedFixedPointProjectMedia.extension || '').toUpperCase() }} • {{ selectedFixedPointProjectMedia.human_readable_size || '--' }}
            </div>
            <div class="file-meta text-muted small mt-1">
              {{ selectedFixedPointProjectMedia.file_name || selectedFixedPointProjectMedia.name || '--' }}
            </div>
            <div class="file-meta text-muted small mt-1">
              {{ selectedFixedPointProjectMedia.uploader ? selectedFixedPointProjectMedia.uploader.name : '' }}
            </div>
            <div class="file-meta text-muted small mt-1">
              {{ selectedFixedPointProjectMedia.created_at | formatJalali }}
            </div>
          </div>
          <div class="media-card-actions d-flex flex-row justify-content-center gap-1 w-100">
            <b-button
              v-if="selectedFixedPointProjectMedia.is_image || selectedFixedPointProjectMedia.is_video || selectedFixedPointProjectMedia.is_document"
              v-b-tooltip.hover.top="'معاينة'"
              :href="selectedFixedPointProjectMedia.url"
              target="_blank"
              size="sm"
              variant="info"
            >
              <feather-icon icon="EyeIcon" size="12" />
            </b-button>
            <b-button
              v-b-tooltip.hover.top="'تحميل'"
              :href="selectedFixedPointProjectMedia.download_url || selectedFixedPointProjectMedia.url"
              target="_blank"
              size="sm"
              variant="success"
            >
              <feather-icon icon="DownloadIcon" size="12" />
            </b-button>
          </div>
        </b-card>
      </div>
      <small
        v-else-if="selectedFixedPointId"
        class="text-muted"
      >لا يوجد ملف Raw Data أو ملف المشروع لهذه النقطة</small>
    </div>

    <div
      v-if="surveyUploadMethodId == 3"
      class="mb-3 p-3 bg-light rounded"
    >
      <b-form-group label="نقطة STATIC">
        <b-form-select
          v-model="selectedStaticPointId"
          :options="staticPointsOptions"
          @change="onStaticPointSelected"
        >
          <template #first>
            <b-form-select-option :value="null">
              اختر نقطة STATIC
            </b-form-select-option>
            <b-form-select-option :value="addFixedPointOptionValue">
              أضافة نقطة
            </b-form-select-option>
          </template>
        </b-form-select>
      </b-form-group>
      <div
        v-if="loadingSelectedStaticPointMedia"
        class="text-center my-2"
      >
        <b-spinner
          small
          variant="primary"
        />
      </div>
      <div
        v-else-if="selectedStaticPointRinexMedia || selectedStaticPointRawDataMedia || selectedStaticPointProjectMedia"
        class="mt-2 d-flex flex-wrap"
      >
        <b-card
          v-if="selectedStaticPointRinexMedia"
          class="media-card mb-1 shadow-sm ml-1"
          body-class="p-1 d-flex flex-column align-items-center"
          style="width: 220px; min-height: 260px;"
        >
          <div class="w-100 text-center">
            <div
              class="file-title font-weight-bold"
              style="word-break:break-word"
            >
              ملف RINEX للنقطة
            </div>
          </div>
          <div class="media-file-details w-100 text-center">
            <div class="file-meta text-muted small mt-1">
              {{ (selectedStaticPointRinexMedia.extension || '').toUpperCase() }} • {{ selectedStaticPointRinexMedia.human_readable_size || '--' }}
            </div>
            <div class="file-meta text-muted small mt-1">
              {{ selectedStaticPointRinexMedia.file_name || selectedStaticPointRinexMedia.name || '--' }}
            </div>
            <div class="file-meta text-muted small mt-1">
              {{ selectedStaticPointRinexMedia.uploader ? selectedStaticPointRinexMedia.uploader.name : '' }}
            </div>
            <div class="file-meta text-muted small mt-1">
              {{ selectedStaticPointRinexMedia.created_at | formatJalali }}
            </div>
          </div>
          <div class="media-card-actions d-flex flex-row justify-content-center gap-1 w-100">
            <b-button
              v-if="selectedStaticPointRinexMedia.is_image || selectedStaticPointRinexMedia.is_video || selectedStaticPointRinexMedia.is_document"
              v-b-tooltip.hover.top="'معاينة'"
              :href="selectedStaticPointRinexMedia.url"
              target="_blank"
              size="sm"
              variant="info"
            >
              <feather-icon icon="EyeIcon" size="12" />
            </b-button>
            <b-button
              v-b-tooltip.hover.top="'تحميل'"
              :href="selectedStaticPointRinexMedia.download_url || selectedStaticPointRinexMedia.url"
              target="_blank"
              size="sm"
              variant="success"
            >
              <feather-icon icon="DownloadIcon" size="12" />
            </b-button>
          </div>
        </b-card>

        <b-card
          v-if="selectedStaticPointRawDataMedia"
          class="media-card mb-1 shadow-sm ml-1"
          body-class="p-1 d-flex flex-column align-items-center"
          style="width: 220px; min-height: 260px;"
        >
          <div class="w-100 text-center">
            <div
              class="file-title font-weight-bold"
              style="word-break:break-word"
            >
              ملف Raw Data للنقطة
            </div>
          </div>
          <div class="media-file-details w-100 text-center">
            <div class="file-meta text-muted small mt-1">
              {{ (selectedStaticPointRawDataMedia.extension || '').toUpperCase() }} • {{ selectedStaticPointRawDataMedia.human_readable_size || '--' }}
            </div>
            <div class="file-meta text-muted small mt-1">
              {{ selectedStaticPointRawDataMedia.file_name || selectedStaticPointRawDataMedia.name || '--' }}
            </div>
            <div class="file-meta text-muted small mt-1">
              {{ selectedStaticPointRawDataMedia.uploader ? selectedStaticPointRawDataMedia.uploader.name : '' }}
            </div>
            <div class="file-meta text-muted small mt-1">
              {{ selectedStaticPointRawDataMedia.created_at | formatJalali }}
            </div>
          </div>
          <div class="media-card-actions d-flex flex-row justify-content-center gap-1 w-100">
            <b-button
              v-if="selectedStaticPointRawDataMedia.is_image || selectedStaticPointRawDataMedia.is_video || selectedStaticPointRawDataMedia.is_document"
              v-b-tooltip.hover.top="'معاينة'"
              :href="selectedStaticPointRawDataMedia.url"
              target="_blank"
              size="sm"
              variant="info"
            >
              <feather-icon icon="EyeIcon" size="12" />
            </b-button>
            <b-button
              v-b-tooltip.hover.top="'تحميل'"
              :href="selectedStaticPointRawDataMedia.download_url || selectedStaticPointRawDataMedia.url"
              target="_blank"
              size="sm"
              variant="success"
            >
              <feather-icon icon="DownloadIcon" size="12" />
            </b-button>
          </div>
        </b-card>

        <b-card
          v-if="selectedStaticPointProjectMedia"
          class="media-card mb-1 shadow-sm"
          body-class="p-1 d-flex flex-column align-items-center"
          style="width: 220px; min-height: 260px;"
        >
          <div class="w-100 text-center">
            <div
              class="file-title font-weight-bold"
              style="word-break:break-word"
            >
              ملف المشروع (project)
            </div>
          </div>
          <div class="media-file-details w-100 text-center">
            <div class="file-meta text-muted small mt-1">
              {{ (selectedStaticPointProjectMedia.extension || '').toUpperCase() }} • {{ selectedStaticPointProjectMedia.human_readable_size || '--' }}
            </div>
            <div class="file-meta text-muted small mt-1">
              {{ selectedStaticPointProjectMedia.file_name || selectedStaticPointProjectMedia.name || '--' }}
            </div>
            <div class="file-meta text-muted small mt-1">
              {{ selectedStaticPointProjectMedia.uploader ? selectedStaticPointProjectMedia.uploader.name : '' }}
            </div>
            <div class="file-meta text-muted small mt-1">
              {{ selectedStaticPointProjectMedia.created_at | formatJalali }}
            </div>
          </div>
          <div class="media-card-actions d-flex flex-row justify-content-center gap-1 w-100">
            <b-button
              v-if="selectedStaticPointProjectMedia.is_image || selectedStaticPointProjectMedia.is_video || selectedStaticPointProjectMedia.is_document"
              v-b-tooltip.hover.top="'معاينة'"
              :href="selectedStaticPointProjectMedia.url"
              target="_blank"
              size="sm"
              variant="info"
            >
              <feather-icon icon="EyeIcon" size="12" />
            </b-button>
            <b-button
              v-b-tooltip.hover.top="'تحميل'"
              :href="selectedStaticPointProjectMedia.download_url || selectedStaticPointProjectMedia.url"
              target="_blank"
              size="sm"
              variant="success"
            >
              <feather-icon icon="DownloadIcon" size="12" />
            </b-button>
          </div>
        </b-card>
      </div>
      <small
        v-else-if="selectedStaticPointId"
        class="text-muted"
      >لا توجد ملفات لهذه النقطة</small>
    </div>

    <div
      v-if="surveyUploadMethodId == 4"
      class="mb-3 p-3 bg-light rounded"
    >
      <b-form-group label="نقطة STATIC">
        <b-form-select
          v-model="selectedCorrectedStaticPointId"
          :options="correctedStaticPointsOptions"
          @change="onCorrectedStaticPointSelected"
        >
          <template #first>
            <b-form-select-option :value="null">
              اختر نقطة STATIC
            </b-form-select-option>
            <b-form-select-option :value="addFixedPointOptionValue">
              أضافة نقطة
            </b-form-select-option>
          </template>
        </b-form-select>
      </b-form-group>
    </div>

    <FixedPointForm
      :show="showAddFixedPointModal"
      @close="closeAddFixedPointModal"
      @saved="onFixedPointCreated"
    />
  </div>
</template>

<script>
import {
  BCard,
  BFormRadioGroup,
  BFormGroup,
  BFormSelect,
  BFormSelectOption,
  BSpinner,
  BButton,
  VBTooltip,
} from 'bootstrap-vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import SurveyMethods from '@/api/survey-methods/surveyMethods'
import FixedPointForm from '@/views/modules/fixed-points/FixedPointForm.vue'

const surveyMethodsApi = new SurveyMethods()

export default {
  name: 'SurveyMethodSelector',
  components: {
    BCard,
    BFormRadioGroup,
    BFormGroup,
    BFormSelect,
    BFormSelectOption,
    BSpinner,
    BButton,
    FeatherIcon,
    FixedPointForm,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  filters: {
    formatJalali(value) {
      if (!value) return ''
      const date = new Date(value)
      return date.toLocaleDateString('ar-EG')
    },
  },
  props: {
    projectId: {
      type: [Number, String],
      required: true,
    },
    currentMethodId: {
      type: [Number, String],
      default: null,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      surveyUploadMethodId: null,
      surveyUploadMethods: [],
      loadingSurveyMethods: false,
      savingSurveyMethod: false,
      addFixedPointOptionValue: '__add_new_fixed_point__',
      showAddFixedPointModal: false,
      pendingFixedPointTarget: null,
      previousFixedPointIds: [],
      selectedFixedPointId: null,
      selectedStaticPointId: null,
      selectedCorrectedStaticPointId: null,
      fixedPointsList: [],
      staticPointsList: [],
      rawDataId: null,
      selectedFixedPointRawDataMedia: null,
      selectedFixedPointProjectMedia: null,
      loadingSelectedFixedPointMedia: false,
      selectedStaticPointRinexMedia: null,
      selectedStaticPointRawDataMedia: null,
      selectedStaticPointProjectMedia: null,
      loadingSelectedStaticPointMedia: false,
      isProcessed: false,
      surveyComments: '',
    }
  },
  computed: {
    fixedPointsOptions() {
      return this.fixedPointsList.map(fp => ({
        value: fp.id,
        text: fp.code || `نقطة ${fp.id}`,
      }))
    },
    staticPointsOptions() {
      const list = this.fixedPointsList.filter(fp => fp.accuracy_degree === '.برصد static' && fp.correction_type === 'before')
      return list.map(fp => ({
        value: fp.id,
        text: fp.code || `نقطة ${fp.id}`,
      }))
    },
    correctedStaticPointsOptions() {
      const list = this.fixedPointsList.filter(fp => fp.accuracy_degree === '.برصد static' && fp.correction_type === 'after')
      return list.map(fp => ({
        value: fp.id,
        text: fp.code || `نقطة ${fp.id}`,
      }))
    },
  },
  watch: {
    currentMethodId(newValue) {
      if (newValue != null && newValue !== '') {
        this.surveyUploadMethodId = newValue
      }
    },
  },
  mounted() {
    if (this.currentMethodId != null && this.currentMethodId !== '') {
      this.surveyUploadMethodId = this.currentMethodId
    }
    this.fetchFixedPoints()
    this.fetchSurveyMethods()
    this.loadSurveyMethod()
  },
  methods: {
    emitSelection() {
      this.$emit('updated', {
        survey_method_id: this.surveyUploadMethodId,
        selected_fixed_point_id: this.selectedFixedPointId,
        selected_static_point_id: this.selectedStaticPointId,
        selected_corrected_static_point_id: this.selectedCorrectedStaticPointId,
      })
    },
    async fetchFixedPoints() {
      try {
        const response = await this.$store.dispatch('fixedPoints/listFixedPoints', {
          query: { per_page: 1000 },
        })
        if (response && response.data) {
          const data = response.data.data || response.data
          this.fixedPointsList = Array.isArray(data) ? data : []
          this.staticPointsList = this.fixedPointsList.filter(fp => fp.accuracy_degree === 'static' || fp.coord_type === 'static')
        }
      } catch (error) {
        console.error('Error fetching fixed points:', error)
      }
    },
    async fetchSurveyMethods() {
      this.loadingSurveyMethods = true
      try {
        const response = await surveyMethodsApi.list()
        if (response.data && Array.isArray(response.data)) {
          this.surveyUploadMethods = response.data
            .sort((a, b) => {
              const orderA = a.order || 0
              const orderB = b.order || 0
              return orderA - orderB
            })
            .map(method => ({
              value: Number(method.id),
              text: method.name,
              id: method.id,
            }))
        } else {
          this.surveyUploadMethods = []
        }
      } catch (error) {
        console.error('Failed to fetch survey methods:', error)
        this.surveyUploadMethods = [
          { value: 1, text: 'الرفع كاملاً VRS لجميع نقاط الرفع' },
          { value: 2, text: 'الرفع نقطة ثابتة VRS وتكملة RTK' },
          { value: 3, text: 'الرفع STATIC لأول مرة لذات الثابتة الغير معالجة مع RTK' },
          { value: 4, text: 'الرفع RTK بمعلومية نقطة مصححة ومعتمدة باساس STATIC' },
        ]
      } finally {
        this.loadingSurveyMethods = false
      }
    },
    async loadSurveyMethod() {
      try {
        const response = await this.$store.dispatch('projects/getSurveyMethod', {
          id: this.projectId,
        })

        if (response && response.data && response.success) {
          const { data } = response
          this.surveyUploadMethodId = data.survey_method?.id
          this.selectedFixedPointId = data.selected_fixed_point_id
          this.selectedStaticPointId = data.selected_static_point_id
          this.selectedCorrectedStaticPointId = data.selected_corrected_static_point_id
          this.isProcessed = data.is_processed || false
          this.surveyComments = data.survey_comments || ''

          if (this.selectedFixedPointId) {
            await this.loadSelectedFixedPointRawDataMedia()
          }
          if (this.selectedStaticPointId) {
            await this.loadSelectedStaticPointMedia()
          }
          this.emitSelection()
        }
      } catch (error) {
        if (error.response && error.response.status !== 404) {
          console.error('Error loading survey method:', error)
        }
      }
    },
    async saveSurveyMethod() {
      if (!this.surveyUploadMethodId) return
      this.savingSurveyMethod = true
      try {
        const payload = {
          survey_method_id: this.surveyUploadMethodId,
          selected_fixed_point_id: this.selectedFixedPointId,
          selected_static_point_id: this.selectedStaticPointId,
          selected_corrected_static_point_id: this.selectedCorrectedStaticPointId,
          is_processed: this.isProcessed,
          survey_comments: this.surveyComments,
        }

        await this.$store.dispatch('projects/updateSurveyMethod', {
          id: this.projectId,
          query: payload,
        })
        this.$toast.success('تم حفظ طريقة الرفع المساحي بنجاح')
        this.emitSelection()
      } catch (error) {
        console.error('Error saving survey method:', error)
        this.$toast.error(error.response?.data?.message || 'تعذر حفظ طريقة الرفع المساحي')
      } finally {
        this.savingSurveyMethod = false
      }
    },
    async onSurveyMethodChange() {
      if (this.surveyUploadMethodId) {
        await this.saveSurveyMethod()
      }
    },
    async onFixedPointSelected() {
      if (!this.selectedFixedPointId) {
        this.selectedFixedPointRawDataMedia = null
        this.selectedFixedPointProjectMedia = null
        return
      }

      try {
        const fixedPoint = this.fixedPointsList.find(fp => String(fp.id) === String(this.selectedFixedPointId))
        if (!fixedPoint) return

        if (fixedPoint.raw_data_media_id) {
          this.rawDataId = fixedPoint.raw_data_media_id
        }
        await this.loadSelectedFixedPointRawDataMedia()
        await this.saveSurveyMethod()
      } catch (error) {
        console.error('Failed to save selected_fixed_point_id:', error)
      }
    },
    async loadSelectedFixedPointRawDataMedia() {
      this.selectedFixedPointRawDataMedia = null
      this.selectedFixedPointProjectMedia = null
      if (!this.selectedFixedPointId) return

      this.loadingSelectedFixedPointMedia = true
      try {
        const response = await this.$store.dispatch('fixedPoints/showFixedPoint', {
          id: this.selectedFixedPointId,
        })
        const rawMedia = response?.data?.raw_data_media || null
        const projectMedia = response?.data?.dwg_media || response?.data?.DWG_media || null
        this.selectedFixedPointRawDataMedia = rawMedia
        this.selectedFixedPointProjectMedia = projectMedia
      } catch (error) {
        this.selectedFixedPointRawDataMedia = null
        this.selectedFixedPointProjectMedia = null
      } finally {
        this.loadingSelectedFixedPointMedia = false
      }
    },
    async onStaticPointSelected() {
      if (this.selectedStaticPointId === this.addFixedPointOptionValue) {
        this.openAddFixedPointModal('static_before')
        return
      }
      if (!this.selectedStaticPointId) {
        this.selectedStaticPointRinexMedia = null
        this.selectedStaticPointRawDataMedia = null
        this.selectedStaticPointProjectMedia = null
        return
      }

      await this.loadSelectedStaticPointMedia()
      await this.saveSurveyMethod()
    },
    async loadSelectedStaticPointMedia() {
      this.selectedStaticPointRinexMedia = null
      this.selectedStaticPointRawDataMedia = null
      this.selectedStaticPointProjectMedia = null
      if (!this.selectedStaticPointId) return

      this.loadingSelectedStaticPointMedia = true
      try {
        const response = await this.$store.dispatch('fixedPoints/showFixedPoint', {
          id: this.selectedStaticPointId,
        })
        this.selectedStaticPointRinexMedia = response?.data?.rinex_media || null
        this.selectedStaticPointRawDataMedia = response?.data?.raw_data_media || null
        this.selectedStaticPointProjectMedia = response?.data?.dwg_media || response?.data?.DWG_media || null
      } catch (error) {
        this.selectedStaticPointRinexMedia = null
        this.selectedStaticPointRawDataMedia = null
        this.selectedStaticPointProjectMedia = null
      } finally {
        this.loadingSelectedStaticPointMedia = false
      }
    },
    async onCorrectedStaticPointSelected() {
      if (this.selectedCorrectedStaticPointId === this.addFixedPointOptionValue) {
        this.openAddFixedPointModal('static_after')
        return
      }
      if (!this.selectedCorrectedStaticPointId) return
      await this.saveSurveyMethod()
    },
    openAddFixedPointModal(target = 'static_before') {
      this.pendingFixedPointTarget = target
      this.previousFixedPointIds = (this.fixedPointsList || [])
        .map(fp => Number(fp?.id))
        .filter(id => Number.isFinite(id))
      if (target === 'static_before') {
        this.selectedStaticPointId = null
      } else if (target === 'static_after') {
        this.selectedCorrectedStaticPointId = null
      }
      this.showAddFixedPointModal = true
    },
    closeAddFixedPointModal() {
      this.showAddFixedPointModal = false
    },
    async onFixedPointCreated() {
      this.showAddFixedPointModal = false
      await this.fetchFixedPoints()

      const previousIdsSet = new Set(this.previousFixedPointIds)
      const newPoints = (this.fixedPointsList || []).filter(fp => {
        const id = Number(fp?.id)
        return Number.isFinite(id) && !previousIdsSet.has(id)
      })
      const latestNewPoint = newPoints.sort((a, b) => Number(b.id) - Number(a.id))[0] || null

      if (this.pendingFixedPointTarget === 'static_after') {
        const correctedPoint = latestNewPoint && latestNewPoint.correction_type === 'after'
          ? latestNewPoint
          : (this.fixedPointsList || [])
            .filter(fp => fp.accuracy_degree === '.برصد static' && fp.correction_type === 'after')
            .sort((a, b) => Number(b.id) - Number(a.id))[0]
        if (correctedPoint) {
          this.selectedCorrectedStaticPointId = correctedPoint.id
          await this.onCorrectedStaticPointSelected()
        }
      } else {
        const staticPoint = latestNewPoint && latestNewPoint.correction_type === 'before'
          ? latestNewPoint
          : (this.fixedPointsList || [])
            .filter(fp => fp.accuracy_degree === '.برصد static' && fp.correction_type === 'before')
            .sort((a, b) => Number(b.id) - Number(a.id))[0]
        if (staticPoint) {
          this.selectedStaticPointId = staticPoint.id
          await this.onStaticPointSelected()
        }
      }

      this.pendingFixedPointTarget = null
      this.previousFixedPointIds = []
    },
  },
}
</script>

<style scoped>
.survey-method-selector {
  background-color: #b1ebe7;
}
.media-card {
  border-radius: 12px;
  transition: box-shadow .2s;
  width: 220px;
  min-height: 260px;
}
.media-card:hover {
  box-shadow: 0 6px 32px #3B82F640;
}
.file-title {
  font-size: 0.97rem;
  font-weight: 500;
  color: #172b38;
}
.file-meta {
  font-size: 0.8rem;
}
.gap-1 {
  gap: .4rem;
}
</style>
